import { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { admin } from '../../utils/api';
import { toast } from 'sonner';
import { 
  DollarSign, CreditCard, Building2, Clock, CheckCircle, 
  AlertCircle, Send, RefreshCw, Search, TrendingUp, Wallet,
  BanknoteIcon, ArrowRight
} from 'lucide-react';

const PAYMENT_STATUS = {
  ready: { label: 'Ready for Payout', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  pending_setup: { label: 'Pending Setup', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-800', icon: RefreshCw },
  paid: { label: 'Paid', color: 'bg-emerald-100 text-emerald-800', icon: DollarSign },
  under_review: { label: 'Under Review', color: 'bg-orange-100 text-orange-800', icon: AlertCircle },
};

export default function AdminCharityPayouts({ user, onLogout }) {
  const [charities, setCharities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCharity, setSelectedCharity] = useState(null);
  const [filter, setFilter] = useState('');
  const [payoutAmount, setPayoutAmount] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [totals, setTotals] = useState({ pending: 0, paid: 0, total: 0 });

  useEffect(() => {
    loadCharities();
  }, []);

  const loadCharities = async () => {
    try {
      setLoading(true);
      const response = await admin.charityPayouts();
      const data = response.data;
      setCharities(data.charities || []);
      setTotals(data.totals || { pending: 0, paid: 0, total: 0 });
    } catch (error) {
      toast.error('Failed to load charity payouts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleInitiatePayout = async () => {
    if (!selectedCharity || !payoutAmount) return;
    
    const amount = parseFloat(payoutAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid payout amount');
      return;
    }

    if (amount > selectedCharity.pending_payout) {
      toast.error('Payout amount cannot exceed pending balance');
      return;
    }

    try {
      setActionLoading(true);
      await admin.initiateCharityPayout(selectedCharity.id, amount);
      toast.success(`Payout of $${amount.toFixed(2)} initiated for ${selectedCharity.name}`);
      setPayoutAmount('');
      loadCharities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payout');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredCharities = charities.filter(charity => {
    if (!filter) return true;
    const searchLower = filter.toLowerCase();
    return (
      charity.name?.toLowerCase().includes(searchLower) ||
      charity.primary_contact_email?.toLowerCase().includes(searchLower)
    );
  });

  const getPaymentMethodBadge = (charity) => {
    if (charity.stripe_account_id && charity.stripe_payouts_enabled) {
      return (
        <Badge className="bg-purple-100 text-purple-800">
          <CreditCard className="w-3 h-3 mr-1" />
          Stripe Connected
        </Badge>
      );
    } else if (charity.manual_bank_details) {
      return (
        <Badge className="bg-blue-100 text-blue-800">
          <Building2 className="w-3 h-3 mr-1" />
          Manual Bank
        </Badge>
      );
    }
    return (
      <Badge className="bg-gray-100 text-gray-800">
        <AlertCircle className="w-3 h-3 mr-1" />
        No Payment Setup
      </Badge>
    );
  };

  const getPayoutStatus = (charity) => {
    if (!charity.payment_method) return 'pending_setup';
    if (charity.verification_status === 'under_review') return 'under_review';
    if (charity.pending_payout > 0) return 'ready';
    return 'paid';
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-8 text-white shadow-lg">
          <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Charity Payouts
          </h2>
          <p className="text-green-50 text-lg">Manage charity donations and process payouts</p>
        </div>

        {/* Summary Stats */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-yellow-100 p-3 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Pending Payouts</p>
                  <p className="text-2xl font-bold text-yellow-600">${totals.pending?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-green-100 p-3 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Paid Out</p>
                  <p className="text-2xl font-bold text-green-600">${totals.paid?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-emerald-100 p-3 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Charitable Contributions</p>
                  <p className="text-2xl font-bold text-emerald-600">${totals.total?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search charities..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="pl-10"
                data-testid="search-payouts"
              />
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Charities List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-emerald-600" />
                Charities ({filteredCharities.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading charities...</div>
              ) : filteredCharities.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No charities found</div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {filteredCharities.map((charity) => {
                    const status = getPayoutStatus(charity);
                    const statusConfig = PAYMENT_STATUS[status];
                    const isSelected = selectedCharity?.id === charity.id;
                    
                    return (
                      <button
                        key={charity.id}
                        onClick={() => {
                          setSelectedCharity(charity);
                          setPayoutAmount(charity.pending_payout?.toFixed(2) || '');
                        }}
                        className={`w-full p-4 rounded-lg border text-left transition-all ${
                          isSelected 
                            ? 'border-emerald-500 bg-emerald-50 shadow-md' 
                            : 'border-gray-200 bg-white hover:border-emerald-300'
                        }`}
                        data-testid={`charity-${charity.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 truncate mb-1">
                              {charity.name}
                            </h4>
                            <div className="flex flex-wrap gap-2 mb-2">
                              {getPaymentMethodBadge(charity)}
                              <Badge className={statusConfig.color} variant="secondary">
                                {statusConfig.label}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-gray-600">
                                Pending: <span className="font-medium text-yellow-600">${charity.pending_payout?.toFixed(2) || '0.00'}</span>
                              </span>
                              <span className="text-gray-600">
                                Total: <span className="font-medium text-emerald-600">${charity.total_charitable_contributions?.toFixed(2) || '0.00'}</span>
                              </span>
                            </div>
                          </div>
                          <ArrowRight className={`w-5 h-5 flex-shrink-0 ${
                            isSelected ? 'text-emerald-600' : 'text-gray-400'
                          }`} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payout Panel */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-emerald-600" />
                Process Payout
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedCharity ? (
                <div className="text-center py-12 text-gray-500">
                  <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Select a charity to process payout</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Charity Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 text-lg mb-2">{selectedCharity.name}</h4>
                    <p className="text-sm text-gray-600 mb-3">{selectedCharity.primary_contact_email}</p>
                    {getPaymentMethodBadge(selectedCharity)}
                  </div>

                  {/* Balance Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-700">Pending Payout</p>
                      <p className="text-2xl font-bold text-yellow-700">
                        ${selectedCharity.pending_payout?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                      <p className="text-sm text-emerald-700">Total Paid</p>
                      <p className="text-2xl font-bold text-emerald-700">
                        ${selectedCharity.total_paid?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </div>

                  {/* Payment Method Details */}
                  {selectedCharity.stripe_account_id && selectedCharity.stripe_payouts_enabled ? (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <CreditCard className="w-5 h-5 text-purple-600" />
                        <h5 className="font-medium text-purple-900">Stripe Connect</h5>
                      </div>
                      <p className="text-sm text-purple-700">
                        Account ID: {selectedCharity.stripe_account_id}
                      </p>
                      <p className="text-sm text-purple-600 mt-1">
                        Payouts will be processed automatically via Stripe
                      </p>
                    </div>
                  ) : selectedCharity.manual_bank_details ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <BanknoteIcon className="w-5 h-5 text-blue-600" />
                        <h5 className="font-medium text-blue-900">Manual Bank Transfer</h5>
                      </div>
                      <div className="text-sm text-blue-700 space-y-1">
                        <p>Bank: {selectedCharity.manual_bank_details.bank_name}</p>
                        <p>Account: ****{selectedCharity.manual_bank_details.bank_account_last4}</p>
                        <p>Type: {selectedCharity.manual_bank_details.account_type}</p>
                      </div>
                      <p className="text-sm text-blue-600 mt-2">
                        Manual ACH transfer required
                      </p>
                    </div>
                  ) : (
                    <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-5 h-5 text-gray-600" />
                        <h5 className="font-medium text-gray-700">No Payment Method</h5>
                      </div>
                      <p className="text-sm text-gray-600">
                        This charity has not set up payment information yet.
                      </p>
                    </div>
                  )}

                  {/* Payout Form */}
                  {selectedCharity.payment_method && selectedCharity.pending_payout > 0 && (
                    <div className="border-t pt-4">
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Payout Amount
                      </label>
                      <div className="flex gap-3">
                        <div className="relative flex-1">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max={selectedCharity.pending_payout}
                            value={payoutAmount}
                            onChange={(e) => setPayoutAmount(e.target.value)}
                            className="pl-10"
                            placeholder="0.00"
                            data-testid="payout-amount"
                          />
                        </div>
                        <Button
                          onClick={handleInitiatePayout}
                          disabled={actionLoading || !payoutAmount}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="initiate-payout-btn"
                        >
                          {actionLoading ? (
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Send className="w-4 h-4 mr-2" />
                          )}
                          {actionLoading ? 'Processing...' : 'Initiate Payout'}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Max payout: ${selectedCharity.pending_payout?.toFixed(2)}
                      </p>
                    </div>
                  )}

                  {/* Recent Payouts */}
                  {selectedCharity.recent_payouts?.length > 0 && (
                    <div className="border-t pt-4">
                      <h5 className="font-medium text-gray-900 mb-3">Recent Payouts</h5>
                      <div className="space-y-2">
                        {selectedCharity.recent_payouts.map((payout, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 p-3 rounded">
                            <span className="text-gray-600">
                              {new Date(payout.date).toLocaleDateString()}
                            </span>
                            <span className="font-medium text-emerald-600">
                              ${payout.amount.toFixed(2)}
                            </span>
                            <Badge className={payout.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                              {payout.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
